import { useState, useEffect, useCallback } from 'react';
import { Search, Plus, Filter, ArrowRightLeft, ArrowRight, ArrowDown, Ban, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/native-select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { FormField } from '@/components/common/FormSection';
import { AmountText, FinanceStatusBadge } from '@/components/finance-v2/FinanceBadges';
import type { Transfer, Account, TransferStatus } from '../types/financeV2.types';
import { financeV2Service, type CreateTransferDto, type TransferFilters } from '../services/financeV2Service';
import { getErrorMessage } from '../utils/error.utils';
import { parseDbTimestamp } from '../utils/datetime';

export function FinanceV2TransfersPage() {
  const [transfers, setTransfers] = useState<Transfer[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<TransferFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  const loadTransfers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await financeV2Service.transfers.list({ ...filters, page, limit: 20 });
      setTransfers(response.data.transfers);
      setTotalPages(response.data.pagination.totalPages);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao carregar transferências'));
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => {
    loadTransfers();
    loadAccounts();
  }, [loadTransfers]);

  const loadAccounts = async () => {
    try {
      const response = await financeV2Service.accounts.list({ isActive: true });
      setAccounts(response.data.accounts);
    } catch (err: unknown) {
      console.error('Erro ao carregar contas:', err);
    }
  };

  const handleCancelTransfer = async (transfer: Transfer) => {
    if (!window.confirm('Tem certeza que deseja cancelar esta transferência? Os saldos das contas serão revertidos.')) {
      return;
    }

    try {
      await financeV2Service.transfers.cancel(transfer.id, { reason: 'Cancelado pelo usuário' });
      loadTransfers();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao cancelar transferência'));
    }
  };

  const handleSearch = (searchTerm: string) => {
    setFilters({ ...filters, search: searchTerm });
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({});
    setPage(1);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return parseDbTimestamp(dateString)?.toLocaleDateString('pt-BR') ?? '-';
  };

  const formatDateTime = (dateString: string) => {
    return parseDbTimestamp(dateString)?.toLocaleString('pt-BR') ?? '-';
  };

  const getStatusBadge = (status: TransferStatus) => (
    <FinanceStatusBadge status={status}>{status === 'COMPLETED' ? 'Concluída' : 'Cancelada'}</FinanceStatusBadge>
  );

  const getAccountName = (accountId: string) => {
    const account = accounts.find(a => a.id === accountId);
    return account?.name || 'Conta não encontrada';
  };

  return (
    <div className="flex flex-col gap-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex items-center gap-2">
            <ArrowRightLeft className="size-6 text-muted-foreground" />
            <h1 className="text-2xl font-semibold">Transferências</h1>
          </div>
          <p className="text-sm text-muted-foreground">Gerencie transferências entre contas</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="size-4" /> Nova Transferência
        </Button>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input
            type="text"
            aria-label="Buscar transferências"
            placeholder="Buscar transferências..."
            className="pl-8"
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>
        <Button variant="outline" onClick={() => setShowFilters(!showFilters)} aria-expanded={showFilters}>
          <Filter className="size-4" /> Filtros
        </Button>
      </div>

      {showFilters && (
        <Card size="sm">
          <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <FormField label="Conta Origem" htmlFor="transfers-filter-from">
              <NativeSelect
                id="transfers-filter-from"
                value={filters.fromAccountId || ''}
                onChange={(e) => setFilters({ ...filters, fromAccountId: e.target.value || undefined })}
              >
                <option value="">Todas</option>
                {accounts.map(account => (
                  <option key={account.id} value={account.id}>{account.name}</option>
                ))}
              </NativeSelect>
            </FormField>

            <FormField label="Conta Destino" htmlFor="transfers-filter-to">
              <NativeSelect
                id="transfers-filter-to"
                value={filters.toAccountId || ''}
                onChange={(e) => setFilters({ ...filters, toAccountId: e.target.value || undefined })}
              >
                <option value="">Todas</option>
                {accounts.map(account => (
                  <option key={account.id} value={account.id}>{account.name}</option>
                ))}
              </NativeSelect>
            </FormField>

            <FormField label="Status" htmlFor="transfers-filter-status">
              <NativeSelect
                id="transfers-filter-status"
                value={filters.status || ''}
                onChange={(e) => setFilters({ ...filters, status: e.target.value as TransferStatus || undefined })}
              >
                <option value="">Todos</option>
                <option value="COMPLETED">Concluída</option>
                <option value="CANCELLED">Cancelada</option>
              </NativeSelect>
            </FormField>

            <div className="flex items-end">
              <Button variant="outline" onClick={clearFilters} className="w-full">
                Limpar Filtros
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {error && (
        <div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex flex-col gap-2" aria-busy="true">
          <span className="sr-only">Carregando transferências...</span>
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-14" />)}
        </div>
      ) : transfers.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-10 text-center">
            <ArrowRightLeft className="size-10 text-muted-foreground" aria-hidden="true" />
            <h3 className="text-sm font-medium">Nenhuma transferência encontrada</h3>
            <p className="text-sm text-muted-foreground">Comece criando uma nova transferência.</p>
            <Button className="mt-2" onClick={() => setShowCreateModal(true)}>
              <Plus className="size-4" /> Nova Transferência
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="min-w-0 py-0">
          <div className="min-w-0 overflow-x-auto">
            <Table className="min-w-[760px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Transferência</TableHead>
                  <TableHead>Valor</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Criado em</TableHead>
                  <TableHead>Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transfers.map((transfer) => (
                  <TableRow key={transfer.id}>
                    <TableCell className="whitespace-normal">
                      <div className="flex min-w-0 items-center gap-3">
                        <ArrowRightLeft className="size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
                        <div className="min-w-0">
                          <div className="text-sm font-medium">{getAccountName(transfer.fromAccountId)}</div>
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <ArrowRight className="size-3" aria-label="para" />
                            <span>{getAccountName(transfer.toAccountId)}</span>
                          </div>
                          {transfer.description && (
                            <div className="mt-1 break-words text-xs text-muted-foreground">{transfer.description}</div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <AmountText tone="neutral">{formatCurrency(transfer.amount)}</AmountText>
                    </TableCell>
                    <TableCell className="tabular-nums text-muted-foreground">{formatDate(transfer.transferDate)}</TableCell>
                    <TableCell>{getStatusBadge(transfer.status)}</TableCell>
                    <TableCell className="tabular-nums text-muted-foreground">{formatDateTime(transfer.createdAt)}</TableCell>
                    <TableCell>
                      {transfer.status === 'COMPLETED' && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              onClick={() => handleCancelTransfer(transfer)}
                              aria-label="Cancelar"
                              className="text-destructive hover:text-destructive"
                            >
                              <Ban className="size-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Cancelar</TooltipContent>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="flex flex-wrap items-center justify-between gap-2 border-t px-4 py-3">
              <p className="text-sm text-muted-foreground">Página {page} de {totalPages}</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page === 1}>
                  <ChevronLeft className="size-4" /> Anterior
                </Button>
                <Button variant="outline" size="sm" onClick={() => setPage(page + 1)} disabled={page === totalPages}>
                  Próxima <ChevronRight className="size-4" />
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {showCreateModal && (
        <CreateTransferModal
          accounts={accounts}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            loadTransfers();
          }}
        />
      )}
    </div>
  );
}

// Create Transfer Modal Component
function CreateTransferModal({
  accounts,
  onClose,
  onSuccess
}: {
  accounts: Account[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [formData, setFormData] = useState<CreateTransferDto>({
    fromAccountId: accounts[0]?.id || '',
    toAccountId: accounts[1]?.id || '',
    amount: 0,
    transferDate: new Date().toISOString().split('T')[0],
    description: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (formData.fromAccountId === formData.toAccountId) {
      setError('Conta de origem e destino não podem ser iguais');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await financeV2Service.transfers.create(formData);
      onSuccess();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao criar transferência'));
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const getAccountBalance = (accountId: string) => {
    const account = accounts.find(a => a.id === accountId);
    return account ? formatCurrency(account.currentBalance) : 'R$ 0,00';
  };

  const accountName = (accountId: string) => accounts.find(a => a.id === accountId)?.name;

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent size="lg" fullScreenOnMobile className="overflow-y-auto sm:max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Nova Transferência</DialogTitle>
          <DialogDescription>Movimente saldo entre duas contas.</DialogDescription>
        </DialogHeader>

        {error && (
          <div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm">
            {error}
          </div>
        )}

        <form id="create-transfer-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
          <FormField
            label="Conta de Origem *"
            htmlFor="transfer-from"
            hint={formData.fromAccountId ? `Saldo atual: ${getAccountBalance(formData.fromAccountId)}` : undefined}
          >
            <NativeSelect
              id="transfer-from"
              required
              value={formData.fromAccountId}
              onChange={(e) => setFormData({ ...formData, fromAccountId: e.target.value })}
            >
              {accounts.map(account => (
                <option key={account.id} value={account.id}>
                  {account.name} - Saldo: {formatCurrency(account.currentBalance)}
                </option>
              ))}
            </NativeSelect>
          </FormField>

          <div className="flex justify-center">
            <ArrowDown className="size-6 text-muted-foreground" aria-hidden="true" />
          </div>

          <FormField
            label="Conta de Destino *"
            htmlFor="transfer-to"
            hint={formData.toAccountId ? `Saldo atual: ${getAccountBalance(formData.toAccountId)}` : undefined}
          >
            <NativeSelect
              id="transfer-to"
              required
              value={formData.toAccountId}
              onChange={(e) => setFormData({ ...formData, toAccountId: e.target.value })}
            >
              {accounts.map(account => (
                <option key={account.id} value={account.id}>
                  {account.name} - Saldo: {formatCurrency(account.currentBalance)}
                </option>
              ))}
            </NativeSelect>
          </FormField>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField label="Valor *" htmlFor="transfer-amount">
              <Input
                id="transfer-amount"
                type="number"
                step="0.01"
                required
                min="0.01"
                value={formData.amount || ''}
                onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
              />
            </FormField>

            <FormField label="Data da Transferência *" htmlFor="transfer-date">
              <Input
                id="transfer-date"
                type="date"
                required
                value={formData.transferDate}
                onChange={(e) => setFormData({ ...formData, transferDate: e.target.value })}
              />
            </FormField>
          </div>

          <FormField label="Descrição" htmlFor="transfer-description">
            <Textarea
              id="transfer-description"
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Motivo ou observação sobre a transferência"
            />
          </FormField>

          {formData.amount > 0 && formData.fromAccountId && formData.toAccountId && (
            <div className="rounded-lg border bg-muted/40 p-3 text-sm">
              <div className="mb-2 font-medium">Resumo da Transferência:</div>
              <dl className="space-y-1">
                <div><dt className="inline font-medium">Valor:</dt> <dd className="inline tabular-nums">{formatCurrency(formData.amount)}</dd></div>
                <div><dt className="inline font-medium">De:</dt> <dd className="inline break-words">{accountName(formData.fromAccountId)}</dd></div>
                <div><dt className="inline font-medium">Para:</dt> <dd className="inline break-words">{accountName(formData.toAccountId)}</dd></div>
              </dl>
            </div>
          )}
        </form>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button type="submit" form="create-transfer-form" disabled={loading}>
            {loading ? 'Criando...' : 'Criar Transferência'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
